import { useLazyQuery, useMutation } from '@apollo/client';
import React, { useContext, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { AppContext } from '../../../../../AppContext';
import { fileUpload, openNotification } from '../../../../../common/utils';
import { CREATE_TOY } from '../graphql/Mutations';
import {
  GET_TOYS_SIGNED_URL,
  GET_TOY_VIDEO_SIGNED_PUT_URL,
} from '../graphql/Queries';
import {
  buildChecklistInput,
  buildRequiredContentsInput,
} from '../toyContentUtils';
import ToyForm from './ToyForm';

const ToyCreateContent = ({
  form,
  onSaved,
  onDirtyChange,
  onSubmittingChange,
}) => {
  const { dispatch } = useContext(AppContext);
  const [toyImages, setToyImages] = useState([]);
  const [toyVideo, setToyVideo] = useState([]);
  const [featuredImage, setFeaturedImage] = useState([]);

  const setIsSubmitLoading = (value) => {
    if (onSubmittingChange) {
      onSubmittingChange(value);
    }
  };

  const [getToysSignUrl] = useLazyQuery(GET_TOYS_SIGNED_URL, {
    fetchPolicy: 'network-only',
    onError() {
      setIsSubmitLoading(false);
    },
  });

  const [getToyVideoSignedPutUrl] = useLazyQuery(GET_TOY_VIDEO_SIGNED_PUT_URL, {
    fetchPolicy: 'network-only',
    onError() {
      setIsSubmitLoading(false);
    },
  });

  const [createToy] = useMutation(CREATE_TOY, {
    onError: () => {
      setIsSubmitLoading(false);
    },
  });

  const createToyFun = async (values) => {
    setIsSubmitLoading(true);
    const uuid = uuidv4();
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: false,
    });
    const toyObj = {
      name: values?.name?.trim(),
      description: values?.description?.trim(),
      ageGroupIds: values?.ageGroup,
      categoryIds: values?.category,
      brandId: values?.brand,
      materialId: values?.material,
      imageUuid: uuid,
      url: values?.url,
      shortDescription: values?.shortDescription?.trim(),
      eanNumber: values?.eanNumber?.trim(),
      price: parseFloat(values?.price),
      carbonEmission: parseFloat(values?.carbonEmission),
      toyNumber: values?.id?.trim(),
      facilitateIds: values?.facilitates,
      tagIds: values?.tags,
      genreIds: values?.genres,
      includedContentMasters: values?.includedContentMasters,
      requiredContents: buildRequiredContentsInput(values?.requiredContents),
      checklist: buildChecklistInput(values?.checklist),
      dimensions: values?.dimensions,
      type: values?.type,
      bagId: values?.packagingMaterial || null,
      slug: values?.slug,
    };
    let images = [];
    let video = '';
    if (values?.video) {
      const { name } = values?.video?.[0];
      const ext = name?.substring(name?.lastIndexOf('.') + 1);
      const timestamp = Date?.now();
      const filename = name?.split('.')?.slice(0, -1)?.join('.');
      const newFilename = `${timestamp}_${filename}.${ext}`;
      const fileKey = `toy/${uuid}/${newFilename}`;

      const res = await getToyVideoSignedPutUrl({
        variables: {
          data: {
            fileName: fileKey,
            imageUuid: uuid,
          },
        },
      });
      if (res?.data) {
        try {
          try {
            await fileUpload(
              res?.data?.getToyVideoSignedPutUrl?.signedUrl,
              toyVideo?.[0]?.originFileObj,
            );
            video = res?.data?.getToyVideoSignedPutUrl?.fileName;
          } catch (error) {
            throw new Error(
              `${toyVideo?.name} upload failed. Please try again.`,
            );
          }
        } catch (error) {
          setIsSubmitLoading(false);
          openNotification('error', error.message);
        }
      }
    }
    if (values?.images) {
      const fileKeyArray = values?.images?.map((item) => {
        const { name } = item;
        const ext = name?.substring(name?.lastIndexOf('.') + 1);
        const timestamp = Date?.now();
        const filename = name?.split('.')?.slice(0, -1)?.join('.');
        const newFilename = `${timestamp}_${filename}.${ext}`;
        const fileKey = `toy/${uuid}/${newFilename}`;

        return fileKey;
      });

      const featuredImageArray = values?.featuredImages?.map((item) => {
        const { name } = item;
        const ext = name?.substring(name?.lastIndexOf('.') + 1);
        const timestamp = Date?.now();
        const filename = name?.split('.')?.slice(0, -1)?.join('.');
        const newFilename = `${timestamp}_${filename}.${ext}`;
        const fileKey = `toy/${uuid}/${newFilename}`;

        return fileKey;
      });

      const newArray = featuredImageArray?.concat(fileKeyArray);

      const res = await getToysSignUrl({
        variables: {
          data: {
            fileNames: newArray,
            imageUuid: uuid,
          },
        },
      });

      const toyAllImages = values?.featuredImages?.concat(values?.images);

      if (res?.data) {
        try {
          const response = await Promise.all(
            res?.data?.getToyImagesSignedPutUrl?.map(
              async (signedData, index) => {
                try {
                  await fileUpload(
                    signedData?.signedUrl,
                    toyAllImages?.[index]?.originFileObj,
                  );
                  return {
                    contentType: toyAllImages?.[index]?.type,
                    key: signedData?.fileName,
                    order: index + 1,
                    size: `${toyAllImages?.[index]?.size}`,
                  };
                } catch (error) {
                  throw new Error(
                    `${toyAllImages?.[index]?.name} upload failed. Please try again.`,
                  );
                }
              },
            ),
          );

          images = response;
        } catch (error) {
          setIsSubmitLoading(false);
          openNotification('error', error.message);
        }
      }
    }
    try {
      const response = await createToy({
        variables: { data: { ...toyObj, images, videoKey: video } },
      });
      if (response?.data) {
        if (onSaved) {
          onSaved();
        }
      }
    } catch (error) {
      setIsSubmitLoading(false);
      return error;
    }
    setIsSubmitLoading(false);
  };

  const handleShowPrompt = () => {
    if (onDirtyChange) {
      onDirtyChange(true);
    }
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: true,
    });
  };


  return (
    <div className="toy-form">
      <ToyForm
        form={form}
        onFinish={createToyFun}
        onSubmittingChange={onSubmittingChange}
        handleShowPrompt={handleShowPrompt}
        toyImages={toyImages}
        setToyImages={setToyImages}
        toyVideo={toyVideo}
        setToyVideo={setToyVideo}
        featuredImage={featuredImage}
        setFeaturedImage={setFeaturedImage}
      />
    </div>
  );
};

export default ToyCreateContent;
